<template>
    <page-shell
        title="Dashboard"
        subtitle="Booking insights with the same access scope and filters as Bookings."
    >
    <div ref="dashboardRoot" class="dashboard-wrapper">
        <template v-if="locations.length > 0 && experiences.length > 0">
            <booking-filters :locations="locations" :experiences="experiences" :stats="statsData" />

            <template v-if="!loadFailed">
                <section class="dashboard-filtered">
                    <header class="section-header">
                        <h2>Selected period</h2>
                        <p>Follows the date, location, experience and options filters above.</p>
                    </header>

                    <dashboard-summary-cards :summary="selectedPeriodSummary" />

                    <div class="chart-grid">
                        <dashboard-selected-range-panel
                            :title="selectedRangeChartTitle"
                            :selected-range-label="selectedRangeLabel"
                            :labels="selectedRangeLabels"
                            :datasets="selectedRangeDatasets"
                        />
                        <dashboard-this-week-panel
                            :weekday-labels="weekdayLabels"
                            :datasets="thisWeekDatasets"
                        />
                        <dashboard-previous-weeks-panel
                            :weekday-labels="weekdayLabels"
                            :datasets="previousWeeksDatasets"
                        />
                        <dashboard-month-comparison-panel
                            :labels="monthComparisonLabels"
                            :datasets="monthComparisonDatasets"
                        />
                        <dashboard-games-panel
                            v-if="filters.experienceId === 'all'"
                            :top-games="topGames"
                        />
                        <dashboard-popular-panels
                            :selected-range-label="selectedRangeLabel"
                            :day-labels="popularDayLabels"
                            :day-datasets="popularDayDatasets"
                        />
                    </div>
                </section>
            </template>

            <dashboard-last-month-section
                :applicable="singleLocationReportScope"
                :ready="lastMonthReady"
                :loading="lastMonthLoading"
                :error="lastMonthError"
                v-bind="lastMonthView || {}"
            />

            <dashboard-fixed-report-section
                :applicable="singleLocationReportScope"
                :ready="fixedReportReady"
                :loading="fixedReportLoading"
                :error="fixedReportError"
                v-bind="fixedReportView || {}"
            />

            <dashboard-location-pricing-section
                :applicable="singleLocationReportScope"
                :ready="pricingReady"
                :loading="pricingLoading"
                :error="pricingError"
                v-bind="pricingView || {}"
            />

            <download-csv-bar :locations="locations" :experiences="experiences" />
        </template>

        <spinner :loading="bootstrapLoading || dataLoading" text="Loading dashboard..." />
    </div>
    </page-shell>
</template>

<script setup>
/**
 * Dashboard orchestrator:
 * - Selected period: summary cards + filter-driven charts
 * - Last month + 12-month + location pricing: single location only
 */
import { Message } from "element-ui";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
    buildLocationScopedFilters,
    fetchDashboardStatsData,
    fetchFixedReportStats,
    fetchLastMonthOverviewStats,
} from "../../helpers/dashboardBookingLoader";
import {
    buildExperienceNameById,
    buildSelectedPeriodSummary,
    isSingleLocationReportScope,
    resolveReportLocationId,
} from "../../helpers/dashboardAggregations";
import { buildDashboardChartViewModel } from "../../helpers/dashboardChartDatasets";
import { buildFixedReportViewModel } from "../../helpers/dashboardFixedReportCharts";
import { buildLastMonthOverviewViewModel } from "../../helpers/dashboardLastMonthCharts";
import { buildLocationPricingViewModel } from "../../helpers/dashboardLocationPricing";
import { readDashboardChartColors } from "../../helpers/dashboardChartColors";
import { getDashboardComparisonPeriods } from "../../helpers/dashboardPeriods";
import { getIsoWeekShortWeekdayLabels } from "../../helpers/dashboardWeekdayLabels";
import { getPrices } from "../../api/rest";
import { getExperiencesSimple } from "../../api/graphql/experiences";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import { useUrlFilters } from "../../composables/useUrlFilters";
import BookingFilters from "../booking/Filters.vue";
import DownloadCsvBar from "../booking/DownloadCsvBar.vue";
import DashboardGamesPanel from "../dashboard/DashboardGamesPanel.vue";
import DashboardFixedReportSection from "../dashboard/DashboardFixedReportSection.vue";
import DashboardLastMonthSection from "../dashboard/DashboardLastMonthSection.vue";
import DashboardLocationPricingSection from "../dashboard/DashboardLocationPricingSection.vue";
import DashboardKpiCards from "../dashboard/DashboardKpiCards.vue";
import DashboardMonthComparisonPanel from "../dashboard/DashboardMonthComparisonPanel.vue";
import DashboardPopularPanels from "../dashboard/DashboardPopularPanels.vue";
import DashboardPreviousWeeksPanel from "../dashboard/DashboardPreviousWeeksPanel.vue";
import DashboardSelectedRangePanel from "../dashboard/DashboardSelectedRangePanel.vue";
import DashboardSummaryCards from "../dashboard/DashboardSummaryCards.vue";
import DashboardThisWeekPanel from "../dashboard/DashboardThisWeekPanel.vue";
import PageShell from "../shared/PageShell.vue";
import Spinner from "../shared/Spinner.vue";

dayjs.extend(isoWeek);

const dashboardRoot = ref(null);
const locations = ref([]);
const experiences = ref([]);
const bootstrapLoading = ref(true);
const dataLoading = ref(false);
const fixedReportLoading = ref(false);
const fixedReportError = ref("");
const lastMonthLoading = ref(false);
const lastMonthError = ref("");
const pricingLoading = ref(false);
const pricingError = ref("");
const loadFailed = ref(false);
const statsData = ref({ count: 0 });
const allPrices = ref([]);

const thisWeekIso = ref(dayjs().isoWeek());
const selectedRangeLabel = ref("");
const selectedRangeChartTitle = ref("Bookings by day (selected timeline)");
const weeklyKPI = ref({ thisWeek: 0, previousWeek: 0, sameWeekLastYear: 0 });
const monthlyKPI = ref({
    previousMonth: 0,
    monthBefore: 0,
    sameMonthLastYear: 0,
    previousMonthLabel: "",
    monthBeforeLabel: "",
    sameMonthLastYearLabel: "",
});
const yearToDateKPI = ref({ current: 0, previous: 0 });
const topGames = ref([]);

const weekdayLabels = ref(getIsoWeekShortWeekdayLabels());
const selectedRangeLabels = ref([]);
const selectedRangeDatasets = ref([]);
const thisWeekDatasets = ref([]);
const previousWeeksDatasets = ref([]);
const monthComparisonLabels = ref([]);
const monthComparisonDatasets = ref([]);
const popularDayLabels = ref([]);
const popularDayDatasets = ref([]);
const fixedReportView = ref(null);
const lastMonthView = ref(null);
const pricingView = ref(null);
let dashboardLoadGeneration = 0;

const fixedReportReady = computed(() => fixedReportView.value != null);
const lastMonthReady = computed(() => lastMonthView.value != null);
const pricingReady = computed(() => pricingView.value != null);

const filterStore = useUrlFilters(
    () => locations.value,
    () => experiences.value,
);
const { filters } = filterStore;

const selectedPeriodSummary = computed(() => {
    const locationId = resolveReportLocationId(
        filters.value?.locationId ?? "all",
        locations.value,
    );
    return buildSelectedPeriodSummary(statsData.value, locationId);
});

const singleLocationReportScope = computed(() =>
    isSingleLocationReportScope(filters.value?.locationId ?? "all", locations.value.length),
);

function resetChartProps() {
    selectedRangeLabels.value = [];
    selectedRangeDatasets.value = [];
    thisWeekDatasets.value = [];
    previousWeeksDatasets.value = [];
    monthComparisonLabels.value = [];
    monthComparisonDatasets.value = [];
    popularDayLabels.value = [];
    popularDayDatasets.value = [];
    topGames.value = [];
    weeklyKPI.value = { thisWeek: 0, previousWeek: 0, sameWeekLastYear: 0 };
    monthlyKPI.value = {
        previousMonth: 0,
        monthBefore: 0,
        sameMonthLastYear: 0,
        previousMonthLabel: "",
        monthBeforeLabel: "",
        sameMonthLastYearLabel: "",
    };
    yearToDateKPI.value = { current: 0, previous: 0 };
}

function applyChartViewModel(vm) {
    weeklyKPI.value = vm.weeklyKPI;
    monthlyKPI.value = vm.monthlyKPI;
    yearToDateKPI.value = vm.yearToDateKPI;
    selectedRangeChartTitle.value = vm.selectedRangeChartTitle;
    selectedRangeLabels.value = vm.selectedRangeLabels;
    selectedRangeDatasets.value = vm.selectedRangeDatasets;
    thisWeekDatasets.value = vm.thisWeekDatasets;
    previousWeeksDatasets.value = vm.previousWeeksDatasets;
    monthComparisonLabels.value = vm.monthComparisonLabels;
    monthComparisonDatasets.value = vm.monthComparisonDatasets;
    topGames.value = vm.topGames;
    popularDayLabels.value = vm.popularDayLabels;
    popularDayDatasets.value = vm.popularDayDatasets;
}

async function loadDashboardData() {
    if (!locations.value.length || !experiences.value.length || !filters.value) {
        return;
    }
    const loadId = ++dashboardLoadGeneration;
    dataLoading.value = true;
    loadFailed.value = false;
    resetChartProps();
    try {
        const base = filters.value;
        const periods = getDashboardComparisonPeriods();

        thisWeekIso.value = periods.thisWeekIso;
        weekdayLabels.value = getIsoWeekShortWeekdayLabels(periods.now);
        selectedRangeLabel.value = `${dayjs(base.startDate).format("D MMM YYYY")} - ${dayjs(base.endDate).format("D MMM YYYY")}`;

        const statsPayload = await fetchDashboardStatsData(base, periods);
        if (loadId !== dashboardLoadGeneration) {
            return;
        }
        statsData.value = statsPayload.filterStats;

        const colors = readDashboardChartColors(dashboardRoot.value);
        const vm = buildDashboardChartViewModel({
            colors,
            baseFilters: base,
            periods,
            statsData: statsPayload,
            experienceNameById: buildExperienceNameById(experiences.value),
            weekdayLabels: weekdayLabels.value,
        });
        applyChartViewModel(vm);

        if (loadId !== dashboardLoadGeneration) {
            return;
        }
        dataLoading.value = false;
        await nextTick();
    } catch (e) {
        if (loadId !== dashboardLoadGeneration) {
            return;
        }
        const msg = e instanceof Error ? e.message : "Could not load dashboard data.";
        Message.error(msg);
        loadFailed.value = true;
        dataLoading.value = false;
    }
}

async function loadLastMonthOverview() {
    if (!locations.value.length || !filters.value) {
        return;
    }
    if (!singleLocationReportScope.value) {
        lastMonthView.value = null;
        lastMonthLoading.value = false;
        lastMonthError.value = "";
        return;
    }
    lastMonthLoading.value = true;
    lastMonthError.value = "";
    try {
        const scoped = buildLocationScopedFilters(filters.value);
        const payload = await fetchLastMonthOverviewStats(scoped);
        const colors = readDashboardChartColors(dashboardRoot.value);
        lastMonthView.value = buildLastMonthOverviewViewModel({
            colors,
            period: payload.period,
            yourBookingsByDay: payload.yourBookingsByDay,
            yourMonthTotal: payload.yourMonthTotal,
            filterStats: payload.filterStats,
        });
    } catch (e) {
        lastMonthView.value = null;
        lastMonthError.value = e instanceof Error ? e.message : "Could not load last month overview.";
        Message.error(lastMonthError.value);
    } finally {
        lastMonthLoading.value = false;
    }
}

async function ensurePricesLoaded() {
    if (allPrices.value.length) {
        return;
    }
    allPrices.value = await getPrices();
}

async function loadLocationPricing() {
    if (!locations.value.length || !filters.value) {
        return;
    }
    if (!singleLocationReportScope.value) {
        pricingView.value = null;
        pricingLoading.value = false;
        pricingError.value = "";
        return;
    }
    pricingLoading.value = true;
    pricingError.value = "";
    try {
        await ensurePricesLoaded();
        const locationId = filters.value.locationId === "all"
            ? locations.value[0]?.id
            : filters.value.locationId;
        const location = locations.value.find(
            (item) => String(item.id) === String(locationId),
        );
        pricingView.value = buildLocationPricingViewModel({
            allPrices: allPrices.value,
            locationId,
            locationName: location?.name || "",
            experienceNameById: buildExperienceNameById(experiences.value),
        });
    } catch (e) {
        pricingView.value = null;
        pricingError.value = e instanceof Error ? e.message : "Could not load location pricing.";
        Message.error(pricingError.value);
    } finally {
        pricingLoading.value = false;
    }
}

async function loadFixedReportData() {
    if (!locations.value.length || !filters.value) {
        return;
    }
    if (!singleLocationReportScope.value) {
        fixedReportView.value = null;
        fixedReportLoading.value = false;
        fixedReportError.value = "";
        return;
    }
    fixedReportLoading.value = true;
    fixedReportError.value = "";
    try {
        const scoped = buildLocationScopedFilters(filters.value);
        const payload = await fetchFixedReportStats(scoped);
        const colors = readDashboardChartColors(dashboardRoot.value);
        fixedReportView.value = buildFixedReportViewModel({
            colors,
            periodLabel: payload.periodLabel,
            monthKeys: payload.monthKeys,
            tableStats: payload.filterStats,
            statsPayload: payload,
            experienceNameById: buildExperienceNameById(experiences.value),
            weekdayLabels: getIsoWeekShortWeekdayLabels(),
        });
    } catch (e) {
        fixedReportView.value = null;
        fixedReportError.value = e instanceof Error ? e.message : "Could not load 12-month report.";
        Message.error(fixedReportError.value);
    } finally {
        fixedReportLoading.value = false;
    }
}

watch(filters, () => {
    loadDashboardData();
}, { immediate: true });

watch(
    () => [filters.value?.locationId, locations.value.length],
    () => {
        loadLastMonthOverview();
        loadFixedReportData();
        loadLocationPricing();
    },
    { immediate: true },
);

onMounted(async () => {
    bootstrapLoading.value = true;
    let bootstrapFailed = false;
    try {
        const [loc, exp] = await Promise.all([
            getUserLocationsSimple(),
            getExperiencesSimple(),
        ]);
        locations.value = loc;
        experiences.value = exp;
        try {
            await ensurePricesLoaded();
        } catch (priceError) {
            // Pricing is optional for charts; surface later in pricing/revenue cards.
            console.warn(priceError);
        }
    } catch (e) {
        bootstrapFailed = true;
        const msg = e instanceof Error ? e.message : "Could not load locations or experiences.";
        Message.error(msg);
    } finally {
        bootstrapLoading.value = false;
    }
    if (
        !bootstrapFailed &&
        (locations.value.length === 0 || experiences.value.length === 0)
    ) {
        Message.warning(
            "Dashboard filters are not available. You need at least one location and experiences (same as the Bookings page).",
        );
    }
    readDashboardChartColors(dashboardRoot.value);
});
</script>

<style scoped lang="scss">
@use "../../colors" as *;
@use "../../style/dashboard-chart-tokens" as tokens;

.dashboard-wrapper {
    @include tokens.dashboard-chart-custom-properties;
    width: 100%;
    min-width: 0;
    color: $color-text-regular;
    text-align: left;
}

.section-header {
    margin: 1rem 0 0.75rem;

    h2 {
        margin: 0 0 0.25rem;
        font-size: 1.2rem;
        color: $color-primary;
    }

    p {
        margin: 0;
        font-size: 0.9rem;
        color: $color-text-primary;
    }
}

/* Six chart panels: 1 column on narrow, 2 columns from 900px (pairs left/right). */
.chart-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
    margin-top: 0.75rem;
    align-items: start;
}

.chart-grid > :deep(*) {
    margin-top: 0 !important;
    min-width: 0;
    width: 100%;
    max-width: 100%;
}

@media (min-width: 900px) {
    .chart-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
